import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Calls `onIdle` after `timeoutMs` with no user activity (mouse, keyboard, touch, scroll).
 */
export function useIdleTimeout(
  onIdle: () => void,
  timeoutMs: number,
  enabled: boolean,
): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    let throttleId: ReturnType<typeof setTimeout> | null = null;

    const scheduleIdle = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => onIdleRef.current(), timeoutMs);
    };

    const handleActivity = () => {
      if (throttleId) {
        return;
      }

      throttleId = setTimeout(() => {
        throttleId = null;
        scheduleIdle();
      }, ACTIVITY_THROTTLE_MS);
    };

    scheduleIdle();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearTimeout(timeoutId);
      if (throttleId) {
        clearTimeout(throttleId);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, timeoutMs]);
}
